import React, { useEffect, useState } from 'react';
import { AppProvider, useAppContext } from './context/AppContext';
import AssistantWidget from './components/AssistantWidget';
import PmsView from './components/PmsView';

const WIDGET_HEIGHT = 600; // Height of the expanded AssistantWidget

function AppContent() {
  const { state } = useAppContext();
  const [accessTokenValidated, setAccessTokenValidated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    const validateAccessToken = () => {
      try {
        // Check if running in local development mode
        const isLocalDev = process.env.NODE_ENV === 'development' || 
                          process.env.REACT_APP_LOCAL_MODE === 'true';
        
        if (isLocalDev) {
          // Local development mode - access token must be provided in URL
          const urlParams = new URLSearchParams(window.location.search);
          const accessToken = urlParams.get('access_token');
          
          if (!accessToken) {
            console.error('❌ No access token provided in URL for local development');
            setValidationError('Access denied');
            setLoading(false);
            return;
          }
          
          // Validate UUID format even in local development
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (!uuidRegex.test(accessToken)) {
            console.error('❌ Invalid access token format in local development:', accessToken);
            setValidationError('Access denied');
            setLoading(false);
            return;
          }
          
          console.log('🔓 Local development mode - using access token:', accessToken.substring(0, 8) + '...');
          
          // Store for API calls
          window.VALIDATED_ACCESS_TOKEN = accessToken;
          window.ACCESS_TOKEN_VALIDATED = 'true';
          window.ACCESS_TOKEN_VALIDATION_INFO = { 
            bypassed: true, 
            reason: 'local_development',
            environment: 'development',
            validated: true
          };
          
          setAccessTokenValidated(true);
          setLoading(false);
          return;
        }
        
        // Production mode - check server-validated access token
        const serverValidated = window.ACCESS_TOKEN_VALIDATED === 'true';
        const accessToken = window.VALIDATED_ACCESS_TOKEN;
        const validationInfo = window.ACCESS_TOKEN_VALIDATION_INFO || {};
        
        if (serverValidated && accessToken) {
          console.log('✅ Access token validated by server:', accessToken.substring(0, 8) + '...', validationInfo);
          setAccessTokenValidated(true);
        } else if (validationInfo.bypassed) {
          console.log('🔓 Access token validation bypassed:', validationInfo.reason);
          setAccessTokenValidated(true);
        } else {
          console.error('❌ No valid access token found');
          setValidationError('Access denied');
          // In production, server should have caught this, but fallback
          window.location.href = '/access-denied.html';
          return;
        }
        
        setLoading(false);
        
      } catch (error) {
        console.error('❌ Access token validation error:', error);
        setValidationError(error.message);
        setLoading(false);
      }
    };

    validateAccessToken();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Validating Access</h3>
          <p className="text-gray-600">Please wait while we verify your authentication...</p>
        </div>
      </div>
    );
  }

  // Validation error state
  if (validationError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Access denied</h3>
        </div>
      </div>
    );
  }

  // Access validated - show main app
  if (!accessTokenValidated) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Access denied</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen font-sans">
      {/* Main view, takes up the full space */}
      <PmsView />

      {/* Assistant widget floats over the main view */}
      <div className="absolute bottom-4 right-4 z-50">
        <AssistantWidget />
      </div>
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App; 